import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ChevronDown, Briefcase, X } from 'lucide-react';
import { projectAnalysisApi, type ProjectOption } from '../lib/api/projectAnalysis';
import LoadingSpinner from './common/LoadingSpinner';
import { formatNumberInput, parseNumberInput, parseNumberToFloat } from '../lib/numberFormat';

const CreateAnalysis: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [projectSearch, setProjectSearch] = useState('');
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState({
    project_id: 0,
    name: '',
    description: '',
    initial_estimate: '',
    tax_percentage: '',
    insurance_mode: 'sections' as 'global' | 'sections',
    insurance_percentage: '',
    profit_type: 'percentage_gross' as string,
    profit_percentage: '',
    profit_amount: '',
    has_prepayment: false,
    prepayment_installments: 3,
    prepayment_percentages: ['33.33', '33.33', '33.34'] as string[],
    has_adjustment: false,
    adjustment_predicted_percentage: '',
    has_maintenance_period: false,
    maintenance_duration: '',
    maintenance_retention_percentage: '',
    extra_coefficients: [] as Array<{ name: string; percentage: string }>,
  });

  useEffect(() => {
    projectAnalysisApi
      .projects()
      .then((res) => setProjects(res.data ?? []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  const filteredProjects = useMemo(() => {
    if (!projectSearch.trim()) return projects;
    const q = projectSearch.trim().toLowerCase();
    return projects.filter(
      (p) =>
        p.original_name.toLowerCase().includes(q) ||
        (p.name?.toLowerCase().includes(q) ?? false) ||
        (p.contract_number?.toLowerCase().includes(q) ?? false) ||
        p.status.toLowerCase().includes(q) ||
        (p.initial_estimate && String(p.initial_estimate).includes(q))
    );
  }, [projects, projectSearch]);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === form.project_id),
    [projects, form.project_id]
  );

  const resetHighlight = useCallback(() => {
    setHighlightedIndex(0);
  }, []);

  useEffect(() => {
    if (projectDropdownOpen) {
      setHighlightedIndex(0);
      listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [projectDropdownOpen, projectSearch, filteredProjects.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!projectDropdownOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setProjectDropdownOpen(true);
      return;
    }
    if (e.key === 'Escape') {
      setProjectDropdownOpen(false);
      inputRef.current?.blur();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, filteredProjects.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter' && filteredProjects[highlightedIndex]) {
      e.preventDefault();
      handleSelectProject(filteredProjects[highlightedIndex]);
      return;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.project_id || !form.name.trim()) {
      setError('پروژه و نام آنالیز را وارد کنید.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const n = form.has_prepayment ? Math.max(1, Math.min(24, form.prepayment_installments)) : 0;
      const prepaymentPercentages =
        n > 0 && form.prepayment_percentages.length >= n
          ? form.prepayment_percentages.slice(0, n).map((s) => parseFloat(s) || 0)
          : undefined;
      const estimateVal = form.initial_estimate ? parseNumberToFloat(form.initial_estimate) : null;
      const res = await projectAnalysisApi.store({
        project_id: form.project_id,
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        initial_estimate: estimateVal != null && estimateVal > 0 ? estimateVal : null,
        tax_percentage: form.tax_percentage ? parseFloat(form.tax_percentage) : undefined,
        insurance_mode: form.insurance_mode,
        insurance_percentage: form.insurance_mode === 'global' && form.insurance_percentage ? parseFloat(form.insurance_percentage) : undefined,
        profit_percentage: form.profit_percentage ? parseFloat(form.profit_percentage) : undefined,
        profit_type: form.profit_type,
        profit_amount: form.profit_amount ? parseFloat(form.profit_amount) : undefined,
        has_prepayment: form.has_prepayment,
        prepayment_installments: form.has_prepayment ? n : undefined,
        prepayment_percentages: prepaymentPercentages,
        has_adjustment: form.has_adjustment,
        adjustment_predicted_percentage: form.has_adjustment && form.adjustment_predicted_percentage ? parseFloat(form.adjustment_predicted_percentage) : undefined,
        has_maintenance_period: form.has_maintenance_period,
        maintenance_duration: form.has_maintenance_period && form.maintenance_duration.trim() ? form.maintenance_duration.trim() : undefined,
        maintenance_retention_percentage: form.has_maintenance_period && form.maintenance_retention_percentage ? parseFloat(form.maintenance_retention_percentage) : undefined,
        extra_coefficients: form.extra_coefficients.filter((c) => c.name.trim() && c.percentage !== '').length > 0
          ? form.extra_coefficients.filter((c) => c.name.trim() && c.percentage !== '').map((c) => ({ name: c.name.trim(), percentage: parseFloat(c.percentage) || 0 }))
          : undefined,
      });
      navigate(`/analyses/${res.data.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'خطا در ذخیره');
    } finally {
      setSaving(false);
    }
  };

  const handleSelectProject = (p: ProjectOption) => {
    const est = p.initial_estimate != null && p.initial_estimate !== ''
      ? String(p.initial_estimate).replace(/[,٬\s]/g, '')
      : '';
    setForm((prev) => ({
      ...prev,
      project_id: p.id,
      name: p.original_name + ' - آنالیز بها',
      initial_estimate: est,
    }));
    setProjectSearch(p.original_name);
    setProjectDropdownOpen(false);
  };

  const handleClearProject = () => {
    setForm((prev) => ({ ...prev, project_id: 0, name: '', initial_estimate: '' }));
    setProjectSearch('');
    setProjectDropdownOpen(true);
    inputRef.current?.focus();
  };

  const handleProjectSearchFocus = () => {
    setProjectDropdownOpen(true);
    resetHighlight();
  };

  const handleProjectSearchBlur = () => {
    setTimeout(() => setProjectDropdownOpen(false), 200);
  };

  const displayValue = projectSearch || selectedProject?.original_name || '';

  if (loading) {
    return <LoadingSpinner variant="page" />;
  }

  return (
    <div className="max-w-5xl mx-auto" dir="rtl">
      <div className="mb-5">
        <h1 className="text-xl font-black text-slate-900">آنالیز جدید</h1>
        <p className="text-sm text-slate-500 mt-1">پروژه، برآورد اولیه و تنظیمات سود و مالیات را مشخص کنید</p>
      </div>
      <form
        onSubmit={handleSubmit}
        className="space-y-5 bg-white rounded-xl p-6 lg:p-8 shadow-sm border border-slate-200"
      >
        {/* انتخاب پروژه - با جستجو */}
        <div className="relative">
          <label className="block text-sm font-bold text-gray-700 mb-2">پروژه *</label>
          <div className="relative">
            <Search
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              size={18}
            />
            <input
              ref={inputRef}
              type="text"
              value={displayValue}
              onChange={(e) => {
                setProjectSearch(e.target.value);
                if (!e.target.value) setForm((p) => ({ ...p, project_id: 0 }));
                setProjectDropdownOpen(true);
              }}
              onFocus={handleProjectSearchFocus}
              onBlur={handleProjectSearchBlur}
              onKeyDown={handleKeyDown}
              placeholder="جستجو: نام، شماره قرارداد، وضعیت پروژه..."
              className="w-full pr-10 pl-12 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0a4d29] focus:border-[#0a4d29] outline-none text-right"
              autoComplete="off"
            />
            {form.project_id > 0 ? (
              <button
                type="button"
                onClick={handleClearProject}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                aria-label="پاک کردن انتخاب"
              >
                <X size={18} />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setProjectDropdownOpen(!projectDropdownOpen)}
                className="absolute left-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600"
              >
                <ChevronDown size={20} className={projectDropdownOpen ? 'rotate-180' : ''} />
              </button>
            )}
          </div>

          {projectDropdownOpen && (
            <div
              ref={listRef}
              className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-64 overflow-y-auto"
            >
              {filteredProjects.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  {projects.length === 0 ? 'پروژه‌ای تعریف نشده است.' : 'پروژه‌ای یافت نشد.'}
                </div>
              ) : (
                filteredProjects.map((p, idx) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleSelectProject(p)}
                    className={`w-full text-right px-4 py-3 hover:bg-emerald-50 flex items-start gap-3 border-b border-gray-100 last:border-0 transition-colors ${
                      form.project_id === p.id ? 'bg-emerald-50' : ''
                    } ${highlightedIndex === idx ? 'bg-emerald-50' : ''}`}
                  >
                    <Briefcase size={18} className="text-[#0a4d29] shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="font-bold text-gray-800 block truncate">{p.original_name}</span>
                      <span className="text-xs text-gray-500 block mt-0.5">
                        {[p.contract_number, p.status]
                          .filter(Boolean)
                          .join(' · ')}
                        {p.initial_estimate &&
                          ` · ${Number(p.initial_estimate).toLocaleString('fa-IR')} ریال`}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}

          {form.project_id > 0 && selectedProject && (
            <p className="text-xs text-emerald-600 mt-1.5 font-medium">
              پروژه انتخاب‌شده: {selectedProject.original_name}
              {selectedProject.contract_number && ` (${selectedProject.contract_number})`}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">نام آنالیز *</label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            placeholder="نام آنالیز"
            required
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0a4d29] focus:border-[#0a4d29] text-right"
          />
        </div>

        <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4 space-y-2">
          <label className="block text-sm font-bold text-emerald-900">برآورد اولیه (ریال)</label>
          <p className="text-xs text-emerald-800/80 leading-relaxed">
            مبنای محاسبه ضریب پیمانکار است. مقدار از پروژه پر می‌شود؛ می‌توانید برای این آنالیز اصلاحش کنید.
          </p>
          <div className="relative" dir="ltr">
            <input
              type="text"
              inputMode="decimal"
              value={formatNumberInput(form.initial_estimate)}
              onChange={(e) => setForm((p) => ({ ...p, initial_estimate: parseNumberInput(e.target.value) }))}
              placeholder="مثلاً ۱٬۰۰۰٬۰۰۰٬۰۰۰"
              className="w-full px-4 py-2.5 border border-emerald-300 rounded-lg focus:ring-2 focus:ring-[#0a4d29] focus:border-[#0a4d29] outline-none bg-white text-left font-mono"
            />
          </div>
          {selectedProject?.initial_estimate && form.initial_estimate &&
            String(selectedProject.initial_estimate).replace(/[,٬\s]/g, '') !== form.initial_estimate.replace(/[,٬\s]/g, '') && (
            <p className="text-[11px] text-amber-800">
              با برآورد ثبت‌شده در پروژه متفاوت است — فقط روی این آنالیز اثر دارد.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">توضیحات</label>
          <textarea
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            rows={2}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0a4d29] text-right placeholder:text-gray-400"
            placeholder="توضیحات اختیاری"
          />
        </div>

        <div className="border-t border-gray-200 pt-5">
          <h3 className="text-sm font-bold text-gray-700 mb-4">پیش‌پرداخت و تعدیل</h3>
          <div className="space-y-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.has_prepayment}
                onChange={(e) => setForm((p) => ({ ...p, has_prepayment: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-[#0a4d29] focus:ring-[#0a4d29]"
              />
              <span className="font-bold text-gray-700">پیش‌پرداخت دارد</span>
            </label>
            {form.has_prepayment && (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">تعداد اقساط</label>
                  <input
                    type="number"
                    min={1}
                    max={24}
                    value={form.prepayment_installments}
                    onChange={(e) => {
                      const n = Math.max(1, Math.min(24, parseInt(e.target.value, 10) || 1));
                      const basePct = 100 / n;
                      const defaultPcts = Array.from(
                        { length: n },
                        (_, i) => (i === n - 1 ? (100 - basePct * (n - 1)).toFixed(2) : basePct.toFixed(2))
                      );
                      setForm((p) => ({ ...p, prepayment_installments: n, prepayment_percentages: defaultPcts }));
                    }}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">درصد هر قسط (می‌تواند متفاوت باشد)</label>
                  <div className="space-y-2">
                    {form.prepayment_percentages.slice(0, form.prepayment_installments).map((pct, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 w-16">قسمت {idx + 1}:</span>
                        <input
                          type="number"
                          step="0.01"
                          min={0}
                          max={100}
                          value={pct}
                          onChange={(e) => {
                            const val = e.target.value;
                            setForm((p) => {
                              const arr = [...p.prepayment_percentages];
                              arr[idx] = val;
                              return { ...p, prepayment_percentages: arr };
                            });
                          }}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                        />
                        <span className="text-gray-500">٪</span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    جمع درصدها بهتر است ۱۰۰ باشد. (فعلی:{' '}
                    {form.prepayment_percentages
                      .slice(0, form.prepayment_installments)
                      .reduce((s, p) => s + (parseFloat(p) || 0), 0)
                      .toFixed(1)}
                    ٪)
                  </p>
                </div>
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.has_adjustment}
                onChange={(e) => setForm((p) => ({ ...p, has_adjustment: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-[#0a4d29] focus:ring-[#0a4d29]"
              />
              <span className="font-bold text-gray-700">تعدیل دارد</span>
            </label>
            {form.has_adjustment && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">پیش‌بینی درصد تعدیل</label>
                <input
                  type="number"
                  step="0.01"
                  min={0}
                  max={100}
                  value={form.adjustment_predicted_percentage}
                  onChange={(e) => setForm((p) => ({ ...p, adjustment_predicted_percentage: e.target.value }))}
                  placeholder="مثلاً ۱۵"
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                />
              </div>
            )}
            <label className="flex items-center gap-3 cursor-pointer mt-3">
              <input
                type="checkbox"
                checked={form.has_maintenance_period}
                onChange={(e) => setForm((p) => ({ ...p, has_maintenance_period: e.target.checked }))}
                className="w-4 h-4 rounded border-gray-300 text-[#0a4d29] focus:ring-[#0a4d29]"
              />
              <span className="font-bold text-gray-700">دوره نگهداری دارد</span>
            </label>
            {form.has_maintenance_period && (
              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">مدت دوره نگهداری</label>
                  <input
                    type="text"
                    value={form.maintenance_duration}
                    onChange={(e) => setForm((p) => ({ ...p, maintenance_duration: e.target.value }))}
                    placeholder="مثلاً ۱۲ ماه"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">درصد مبلغ بلوکه شده بعد از پیمان</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={form.maintenance_retention_percentage}
                    onChange={(e) => setForm((p) => ({ ...p, maintenance_retention_percentage: e.target.value }))}
                    placeholder="مثلاً ۵"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                  />
                </div>
              </div>
            )}
            <div className="mt-4">
              <h4 className="text-sm font-bold text-gray-700 mb-2">ضرایب / کسورات اضافی (مثلاً بیمه مسئولیت)</h4>
              {form.extra_coefficients.map((coef, idx) => (
                <div key={idx} className="flex gap-2 items-center mb-2">
                  <input
                    value={coef.name}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      extra_coefficients: p.extra_coefficients.map((c, i) => (i === idx ? { ...c, name: e.target.value } : c)),
                    }))}
                    placeholder="نام (مثلاً بیمه مسئولیت)"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                  />
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={coef.percentage}
                    onChange={(e) => setForm((p) => ({
                      ...p,
                      extra_coefficients: p.extra_coefficients.map((c, i) => (i === idx ? { ...c, percentage: e.target.value } : c)),
                    }))}
                    placeholder="٪"
                    className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                  />
                  <span className="text-gray-500">٪</span>
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, extra_coefficients: p.extra_coefficients.filter((_, i) => i !== idx) }))}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <X size={18} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, extra_coefficients: [...p.extra_coefficients, { name: '', percentage: '' }] }))}
                className="text-sm text-[#0a4d29] font-bold hover:underline"
              >
                + افزودن ضریب/کسورت اضافی
              </button>
            </div>
          </div>
        </div>

        <div className="border-t border-gray-200 pt-5 mt-2">
          <h3 className="text-sm font-bold text-gray-700 mb-4">تنظیمات مالی</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">درصد مالیات</label>
              <input
                type="number"
                step="0.01"
                value={form.tax_percentage}
                onChange={(e) => setForm((p) => ({ ...p, tax_percentage: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                placeholder="مثلاً ۹"
              />
            </div>
            <div className="col-span-2 rounded-xl border border-violet-200 bg-violet-50/60 p-4 space-y-3">
              <p className="text-sm font-bold text-violet-900">بیمه</p>
              <div className="grid grid-cols-2 gap-3">
                <label className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 cursor-pointer ${form.insurance_mode === 'global' ? 'border-violet-400 bg-white' : 'border-violet-100 bg-white/50'}`}>
                  <input
                    type="radio"
                    name="create_insurance_mode"
                    checked={form.insurance_mode === 'global'}
                    onChange={() => setForm((p) => ({ ...p, insurance_mode: 'global' }))}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-bold text-violet-900">کلی</span>
                    <span className="block text-xs text-violet-700">یک درصد روی جمع کل بعد از سود</span>
                  </span>
                </label>
                <label className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 cursor-pointer ${form.insurance_mode === 'sections' ? 'border-violet-400 bg-white' : 'border-violet-100 bg-white/50'}`}>
                  <input
                    type="radio"
                    name="create_insurance_mode"
                    checked={form.insurance_mode === 'sections'}
                    onChange={() => setForm((p) => ({ ...p, insurance_mode: 'sections' }))}
                    className="mt-0.5"
                  />
                  <span>
                    <span className="block text-sm font-bold text-violet-900">تفکیکی</span>
                    <span className="block text-xs text-violet-700">درصد جدا برای هر بخش (خرید، ماشین‌آلات و ...)</span>
                  </span>
                </label>
              </div>
              {form.insurance_mode === 'global' ? (
                <div>
                  <label className="block text-sm font-bold text-violet-900 mb-2">درصد بیمه از جمع کل</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.insurance_percentage}
                    onChange={(e) => setForm((p) => ({ ...p, insurance_percentage: e.target.value }))}
                    className="w-full px-4 py-2.5 border border-violet-200 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none bg-white"
                    placeholder="مثلاً ۱۶"
                  />
                </div>
              ) : (
                <p className="text-xs text-violet-700 leading-relaxed">بعد از ساخت آنالیز، درصد بیمه هر بخش را جداگانه وارد کنید. اگر همه بخش‌ها ۱۰٪ باشند و جمع کل ۱ میلیارد شود، بیمه ۱۰۰ میلیون است.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">نوع سود</label>
              <select
                value={form.profit_type}
                onChange={(e) => setForm((p) => ({ ...p, profit_type: e.target.value }))}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
              >
                <option value="percentage_gross">درصد از بهای پایه</option>
                <option value="percentage_net">درصد از خالص</option>
                <option value="amount">مبلغ ثابت</option>
              </select>
            </div>
            {form.profit_type !== 'amount' ? (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">درصد سود</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.profit_percentage}
                  onChange={(e) => setForm((p) => ({ ...p, profit_percentage: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                  placeholder="مثلاً ۱۵"
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">مبلغ سود (ریال)</label>
                <input
                  type="number"
                  value={form.profit_amount}
                  onChange={(e) => setForm((p) => ({ ...p, profit_amount: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-right focus:ring-2 focus:ring-[#0a4d29] outline-none"
                  placeholder="مبلغ ثابت"
                />
              </div>
            )}
          </div>
        </div>

        {error && <p className="text-sm text-red-600 font-bold">{error}</p>}

        <div className="flex gap-3 pt-2">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2.5 bg-[#0a4d29] text-white font-bold rounded-lg hover:bg-emerald-900 disabled:opacity-50 transition-colors"
          >
            {saving ? 'در حال ذخیره...' : 'ذخیره'}
          </button>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-6 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-bold"
          >
            انصراف
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateAnalysis;
